import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
//import { useTheme } from '@mui/material/styles';
//import useMediaQuery from '@mui/material/useMediaQuery';

// Import the Deepseek service
import deepseekService from '../../services/ai/deepseekService';
// Import FormattedAnswer component
import FormattedAnswer from '../../components/common/FormattedAnswer';

// Mock data for common problems
const commonProblems = {
  'Sprint Failures': [
    'How do I handle a team that consistently misses Sprint goals?',
    'What should I do when high-priority items are added mid-Sprint?',
    'How can we improve our Sprint planning to be more realistic?',
    'How can I prevent the Sprint tasks spill into next Sprint?'
  ],
  'Team Conflicts': [
    'How do I resolve conflicts between team members?',
    'What strategies work for dealing with uncooperative stakeholders?',
    'How can I help team members who are resistant to Scrum practices?'
  ],
  'Backlog Grooming': [
    'What are effective techniques for prioritizing the product backlog?',
    'How detailed should user stories be before sprint planning?',
    'How can we improve our estimation process?'
  ]
};

const curatedFaq = [
  {
    q: 'What is a Sprint Goal and why is it important?',
    a: 'A Sprint Goal is a single objective for the Sprint that provides focus and aligns the team on outcomes. It guides decisions, clarifies priorities, and helps evaluate progress beyond individual tasks.'
  },
  {
    q: 'How should a Daily Scrum be facilitated effectively?',
    a: 'Center the conversation on the Sprint Goal and the work items, not individuals. Walk the board right-to-left, surface impediments, and identify collaboration opportunities. Keep it under 15 minutes.'
  },
  {
    q: 'What makes a good Product Backlog item ready for planning?',
    a: 'Clear value, defined acceptance criteria, small enough to be completed within a Sprint, and understood by the team. Dependencies and risks should be identified and visible.'
  },
  {
    q: 'How do we improve estimation accuracy?',
    a: 'Use consensus-based methods like Planning Poker, discuss outliers to uncover complexity, leverage historical velocity, and separate effort from uncertainty when sizing.'
  },
  {
    q: 'What should be included in a Definition of Done?',
    a: 'All quality checks and activities required for a potentially shippable increment, such as code review, tests passing, documentation updates, security checks, and deployment readiness.'
  }
];

const QandA = () => {
  //const theme = useTheme();
  //const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  //const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));
  
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [category, setCategory] = useState('Sprint Failures');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleQuestionChange = (event) => {
    setQuestion(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (question.trim()) {
      setLoading(true);
      setError('');
      try {
        const response = await deepseekService.getAnswer(question);
        setAnswer(response);
      } catch (err) {
        setError(err.message || 'Failed to get an answer. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCategorySelect = (cat) => {
    setCategory(cat);
  };

  const handleCommonQuestionClick = async (q) => {
    setQuestion(q);
    setLoading(true);
    setError('');
    try {
      const response = await deepseekService.getAnswer(q);
      setAnswer(response);
      // Switch to the Ask a Question tab to display the answer
      setTabValue(0);
    } catch (err) {
      setError(err.message || 'Failed to get an answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Q&amp;A Consultant | ScrumTherapist</title>
        <link rel="canonical" href="https://www.scrumtherapist.com/qanda" />
        <meta name="description" content="Get instant Agile and Scrum answers from our AI consultant." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://www.scrumtherapist.com/qanda#webpage",
            "url": "https://www.scrumtherapist.com/qanda",
            "name": "Q&A Consultant",
            "description": "Get instant Agile and Scrum answers from our AI consultant.",
            "isPartOf": { "@id": "https://www.scrumtherapist.com/#website" }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": curatedFaq.map(item => ({
              "@type": "Question",
              "name": item.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.a
              }
            }))
          })}
        </script>
      </Helmet>
    <Container maxWidth="lg">
      <Box sx={{ my: { xs: 3, md: 4, lg: 5 } }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center" 
          color="primary.dark"
          sx={{ fontSize: { xs: '1.75rem', md: '2.25rem', lg: '2.75rem' } }}
        >
          Scrum Problem-Solving Q&A
        </Typography>
        <Typography 
          variant="h6" 
          align="center" 
          color="text.secondary" 
          paragraph
          sx={{ fontSize: { xs: '0.9rem', md: '1rem', lg: '1.1rem' } }}
        >
          Get expert advice on your Scrum-related challenges
        </Typography>
      </Box>

      <Paper sx={{ mb: { xs: 3, md: 4, lg: 5 }, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
          sx={{ 
            '& .MuiTab-root': { 
              fontSize: { xs: '0.85rem', md: '0.95rem', lg: '1rem' } 
            } 
          }}
        >
          <Tab label="Ask a Question" />
          <Tab label="Common Questions" />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Ask your Scrum question"
                variant="outlined"
                value={question}
                onChange={handleQuestionChange}
                margin="normal"
                disabled={loading}
                sx={{ 
                  '& .MuiInputBase-input': { 
                    fontSize: { xs: '0.9rem', md: '1rem' } 
                  } 
                }}
              />
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={loading || !question.trim()}
                sx={{ 
                  mt: 2,
                  fontSize: { xs: '0.85rem', md: '0.9rem', lg: '1rem' },
                  py: { xs: 1, md: 1.25 },
                  px: { xs: 2, md: 3 }
                }}
              >
                {loading ? 'Getting Answer...' : 'Get Answer'}
              </Button>
              {loading && (
                <CircularProgress 
                  size={24} 
                  sx={{ ml: 2, verticalAlign: 'middle' }} 
                />
              )}
            </form>

            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}

            
            {answer && !loading && !error && (
              <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mt: 3 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
                >
                  Answer:
                </Typography>
                <FormattedAnswer text={answer} />
              </Paper>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            p: 0 
          }}>
            <Box sx={{ 
              width: { xs: '100%', md: '30%' }, 
              borderRight: { xs: 0, md: 1 },
              borderBottom: { xs: 1, md: 0 },
              borderColor: 'divider' 
            }}>
              <List component="nav">
                {Object.keys(commonProblems).map((cat) => (
                  <ListItem 
                    button 
                    key={cat} 
                    onClick={() => handleCategorySelect(cat)}
                    selected={category === cat}
                    sx={{ py: { xs: 1, md: 1.5 } }}
                  >
                    <ListItemText 
                      primary={cat} 
                      primaryTypographyProps={{
                        sx: { fontSize: { xs: '0.9rem', md: '1rem' } }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box sx={{ 
              width: { xs: '100%', md: '70%' }, 
              p: { xs: 1.5, md: 2 } 
            }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
              >
                {category}
              </Typography>
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                  {error}
                </Alert>
              )}
              <List>
                {commonProblems[category].map((problem, index) => (
                  <React.Fragment key={index}>
                    <ListItem 
                      button 
                      onClick={() => handleCommonQuestionClick(problem)}
                      disabled={loading}
                      sx={{ py: { xs: 1, md: 1.5 } }}
                    >
                      <ListItemText 
                        primary={problem} 
                        primaryTypographyProps={{
                          sx: { fontSize: { xs: '0.85rem', md: '0.95rem' } }
                        }}
                      />
                    </ListItem>
                    {index < commonProblems[category].length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </Box>
        )}
      </Paper>
      <Box sx={{ mt: { xs: 2, md: 4 } }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ fontWeight: 'bold' }}
        >
          Curated FAQs
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List>
          {curatedFaq.map((item, idx) => (
            <ListItem key={idx} alignItems="flex-start" sx={{ display: 'block' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {item.q}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.a}
              </Typography>
              {idx < curatedFaq.length - 1 && <Divider sx={{ mt: 2 }} />}
            </ListItem>
          ))}
        </List>
      </Box>
    </Container>
    </>
  );
};

export default QandA;
